/**
 * Signup consent page – Privacy Policy Consent (Image 1) after OAuth signup.
 * Shown only when user just signed up; on Continue saves consent and redirects to agent.
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import PrivacyPolicyConsentScreen from '../components/Auth/PrivacyPolicyConsentScreen';
import { useAuthentication } from '../hooks/useAuthentication';
import { supabase } from '../lib/supabase';

export interface SignupConsentPageProps {
  onNavigate: (view: string) => void;
  onConsentComplete: () => void;
}

const SignupConsentPage: React.FC<SignupConsentPageProps> = ({
  onNavigate,
  onConsentComplete
}) => {
  const { session, signOut } = useAuthentication();

  // If not authenticated, redirect to signup
  useEffect(() => {
    if (session === null) {
      // Still loading
      return;
    }
    if (!session) {
      onNavigate('signup');
    }
  }, [session, onNavigate]);

  const handleContinue = async (consent: { terms: boolean; marketing?: boolean }) => {
    if (!session?.user) return;
    try {
      await supabase.auth.updateUser({
        data: {
          consent_terms_at: new Date().toISOString(),
          consent_marketing: !!consent.marketing
        }
      });
    } catch (e) {
      console.warn('Failed to save consent to user metadata:', e);
    }
    onConsentComplete();
  };

  const handleBackToSignUp = async () => {
    try {
      await signOut();
    } catch (e) {
      console.warn('Sign out error:', e);
    }
    onNavigate('signup');
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Privacy Consent — GoGrabJob</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PrivacyPolicyConsentScreen
        onContinue={handleContinue}
        onBackToSignUp={handleBackToSignUp}
        onNavigate={onNavigate}
      />
    </>
  );
};

export default SignupConsentPage;
